import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import "./theme_selector.scss";

const ThemeSelector = ({
  themes,
  activeThemeName,
  onThemeSelect,
  onCustomThemeClick,
  customThemeName = "new",
  customLabel = "Custom"
}) => {
  const cardRefs = useRef([]);

  const defaultSelection = useMemo(() => {
    const fallback = themes.find((theme) => theme.name !== customThemeName);
    return activeThemeName || fallback?.name || "";
  }, [themes, customThemeName, activeThemeName]);

  const [selectedTheme, setSelectedTheme] = useState(defaultSelection);

  useEffect(() => {
    setSelectedTheme(defaultSelection);
  }, [defaultSelection]);

  const handleSelect = (theme) => {
    if (theme.name === customThemeName) {
      onCustomThemeClick?.(theme);
      return;
    }

    setSelectedTheme(theme.name);
    onThemeSelect?.(theme);
  };

  const focusCardByIndex = (index) => {
    const normalizedIndex = (index + themes.length) % themes.length;
    const target = cardRefs.current[normalizedIndex];

    if (target) {
      target.focus();
    }
  };

  const onKeyDownCard = (event, index, theme) => {
    if (event.key === "ArrowRight" || event.key === "ArrowDown") {
      event.preventDefault();
      focusCardByIndex(index + 1);
      return;
    }

    if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
      event.preventDefault();
      focusCardByIndex(index - 1);
      return;
    }

    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleSelect(theme);
    }
  };

  return (
    <div className="theme-selector" role="radiogroup" aria-label="Theme selector">
      {themes.map((theme, index) => {
        const isCustom = theme.name === customThemeName;
        const isSelected = !isCustom && selectedTheme === theme.name;

        return (
          <button
            key={theme.name}
            type="button"
            ref={(element) => {
              cardRefs.current[index] = element;
            }}
            className={`theme-card ${isSelected ? "is-selected" : ""} ${
              isCustom ? "is-custom" : ""
            }`}
            role="radio"
            aria-checked={isSelected}
            aria-label={isCustom ? customLabel : theme.description}
            onClick={() => handleSelect(theme)}
            onKeyDown={(event) => onKeyDownCard(event, index, theme)}
          >
            {isCustom ? (
              <div className="theme-card__custom-content">
                <div className="theme-card__plus" aria-hidden="true">
                  +
                </div>
                <span className="theme-card__name">{customLabel}</span>
              </div>
            ) : (
              <>
                <div className="theme-card__preview" aria-hidden="true">
                  <div
                    className="theme-card__preview-header"
                    style={{ backgroundColor: theme.colors.secondary }}
                  >
                    <span style={{ backgroundColor: theme.colors.primary }} />
                    <span style={{ backgroundColor: theme.colors.primary }} />
                  </div>
                  <div
                    className="theme-card__preview-content"
                    style={{ backgroundColor: theme.colors.primary }}
                  >
                    <div
                      className="theme-card__bubble theme-card__bubble--agent"
                      style={{ backgroundColor: theme.colors.tertiary }}
                    />
                    <div
                      className="theme-card__bubble theme-card__bubble--user"
                      style={{ backgroundColor: theme.colors.secondary }}
                    />
                  </div>
                </div>
                <div className="theme-card__meta">
                  <span className="theme-card__name">{theme.description}</span>
                </div>
              </>
            )}
          </button>
        );
      })}
    </div>
  );
};

ThemeSelector.propTypes = {
  themes: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      description: PropTypes.string.isRequired,
      colors: PropTypes.shape({
        primary: PropTypes.string,
        secondary: PropTypes.string,
        tertiary: PropTypes.string
      })
    })
  ).isRequired,
  activeThemeName: PropTypes.string,
  onThemeSelect: PropTypes.func,
  onCustomThemeClick: PropTypes.func,
  customThemeName: PropTypes.string,
  customLabel: PropTypes.string
};

export default ThemeSelector;
